import { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import CongressSelector from "@/components/CongressSelector";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileText, Calendar, User, ArrowRight, Tag, Sparkles, Trophy, ArrowRightLeft, ArrowLeftRight, Zap, Scroll, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [congress, setCongress] = useState(() => {
    const saved = localStorage.getItem("capitolPulse.congress");
    return saved ? parseInt(saved, 10) : 119;
  });
  const [filterStatus, setFilterStatus] = useState("all");
  const [billType, setBillType] = useState("all");
  const [statusCounts, setStatusCounts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [policyArea, setPolicyArea] = useState(() => searchParams.get("policyArea") || "all");
  const [policyAreas, setPolicyAreas] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [total, setTotal] = useState(0);
  // Stalled & Forgotten mode, when true, replace the bill grid with a
  // forecast-cache-derived list of bills that have stopped moving.
  const [stalledMode, setStalledMode] = useState(false);
  const { featureMode } = useAuth();
  const stalledLocked = featureMode("stalled_forgotten") === "none";
  const [stalledData, setStalledData] = useState({ bills: [], count: 0 });
  // "Just Introduced" drill-through from the front page, ?introducedWithin=72
  const introducedWithin = searchParams.get("introducedWithin");

  // Sync URL <-> state. When the URL's ?policyArea= changes (e.g. user clicks
  // a tag from a bill page), pull it into state. When the local state changes,
  // push it back to the URL so the filter is shareable & back-button friendly.
  useEffect(() => {
    const fromUrl = searchParams.get("policyArea") || "all";
    if (fromUrl !== policyArea) {
      setPolicyArea(fromUrl);
    }
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get("policyArea") || "all";
    if (current !== policyArea) {
      const next = new URLSearchParams(searchParams);
      if (policyArea === "all") {
        next.delete("policyArea");
      } else {
        next.set("policyArea", policyArea);
      }
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [policyArea]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [congress, filterStatus, debouncedSearch, policyArea, billType, introducedWithin]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Load policy areas whenever the congress changes
  useEffect(() => {
    axios
      .get(`${API}/bills/policy-areas`, { params: { congress }, timeout: 30000 })
      .then((r) => setPolicyAreas(r.data || []))
      .catch(() => setPolicyAreas([]));
  }, [congress]);

  useEffect(() => {
    // Stalled & Forgotten is derived from the LIVE forecast cache which we
    // only populate for the current Congress. Historical congresses don't
    // have forecast snapshots, so auto-disable stalled mode when the user
    // switches away from the current session, otherwise the grid would just
    // render 0 bills with no explanation.
    if (stalledMode && congress !== 119) {
      setStalledMode(false);
      toast.info("Stalled & Forgotten uses live forecasts, only available for the current (119th) Congress.");
      return; // effect re-runs with stalledMode=false
    }
    if (stalledMode) {
      fetchStalled();
    } else {
      fetchBills();
    }
    localStorage.setItem("capitolPulse.congress", String(congress));
  }, [congress, filterStatus, debouncedSearch, page, policyArea, billType, stalledMode, introducedWithin]); // eslint-disable-line

  // Load status counts whenever the non-status filters change, lets us
  // render "(N)" next to each status tab without a call-per-tab.
  useEffect(() => {
    const params = { congress };
    if (billType !== "all") params.type = billType;
    if (policyArea !== "all") params.policyArea = policyArea;
    axios
      .get(`${API}/bills/status-counts`, { params, timeout: 30000 })
      .then((r) => setStatusCounts(r.data || {}))
      .catch(() => setStatusCounts({}));
  }, [congress, billType, policyArea]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const params = {
        congress,
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (filterStatus !== "all") params.status = filterStatus;
      if (debouncedSearch) params.search = debouncedSearch;
      if (policyArea !== "all") params.policyArea = policyArea;
      if (billType !== "all") params.type = billType;
      if (introducedWithin) params.introducedWithinHours = parseInt(introducedWithin, 10) || 72;
      const response = await axios.get(`${API}/bills`, { params });
      setBills(response.data);
      const totalHdr = response.headers?.["x-total-count"] ?? response.headers?.["X-Total-Count"];
      setTotal(totalHdr ? parseInt(totalHdr, 10) : response.data.length);
    } catch (error) {
      toast.error("Failed to load congressional data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStalled = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/bills/stalled`, { params: { limit: 30, min_idle_days: 180 } });
      setStalledData(r.data || { bills: [], count: 0 });
      setTotal((r.data?.bills || []).length);
    } catch (e) {
      toast.error("Failed to load stalled bills");
      setStalledData({ bills: [], count: 0 });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const getStatusColor = (status) => {
    const colors = {
      Enacted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      "Passed House": "bg-[#2F6BFF]/15 text-blue-300 border-[#2F6BFF]/30",
      "Passed Senate": "bg-purple-500/15 text-purple-300 border-purple-500/30",
      "In Progress": "bg-amber-500/15 text-amber-300 border-amber-500/30",
      Introduced: "bg-white/5 text-white/60 border-white/15",
      Failed: "bg-[#FF3B3B]/15 text-rose-300 border-[#FF3B3B]/30",
    };
    return colors[status] || "bg-white/5 text-white/60 border-white/15";
  };

  const getTypeColor = (type) => {
    const colors = {
      HR: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
      S: "bg-orange-500/15 text-orange-300 border-orange-500/30",
      HRES: "bg-teal-500/15 text-teal-300 border-teal-500/30",
      SRES: "bg-pink-500/15 text-pink-300 border-pink-500/30",
    };
    return colors[type] || "bg-white/5 text-white/60 border-white/15";
  };

  if (loading && bills.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading congressional data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation />
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="whip-pill whip-pill-orange" data-testid="dashboard-eyebrow">WHIP COUNT</span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-white/40">Live · Congress.gov</span>
              </div>
              <h1 className="text-4xl font-black tracking-tight text-white" data-testid="dashboard-title">
                Legislation
              </h1>
              <p className="text-sm text-white/55 mt-2">
                Every bill in the active Congress, track movement, vote splits, and probability before it hits the floor.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <CongressSelector value={congress} onChange={setCongress} testId="dashboard-congress-selector" />
              <Badge variant="outline" className="px-4 py-2 text-sm border-white/15 bg-white/5 text-white" data-testid="dashboard-total-badge">
                <FileText className="w-4 h-4 mr-2 text-orange-400" />
                {total.toLocaleString()} Bills
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="mb-8">
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search bills by title, number, or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-base rounded-xl border-2 focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </div>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 z-10 pointer-events-none" />
              <Select value={policyArea} onValueChange={setPolicyArea}>
                <SelectTrigger className="pl-10 py-6 rounded-xl border-2" data-testid="policy-area-select">
                  <SelectValue placeholder="Filter by policy area" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all" data-testid="policy-area-all">All policy areas</SelectItem>
                  {policyAreas.map((p) => (
                    <SelectItem
                      key={p.name}
                      value={p.name}
                      data-testid={`policy-area-${p.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    >
                      {p.name} <span className="text-xs text-gray-500">({p.billCount})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs defaultValue="all-status" className="mb-4">
            <TabsList className="flex flex-wrap h-auto justify-center gap-2">
              <TabsTrigger
                data-testid="filter-all-status"
                value="all-status"
                onClick={() => setFilterStatus("all")}
                className="data-[state=active]:whip-orange-bg data-[state=active]:text-white"
              >
                All Status
                <span className="ml-1.5 text-[11px] text-gray-500 data-[state=active]:text-white/80 tabular-nums">
                  {statusCounts.All != null ? `(${statusCounts.All})` : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                data-testid="filter-enacted"
                value="enacted"
                onClick={() => setFilterStatus("Enacted")}
                className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-white/70"
              >
                Enacted
                <span className="ml-1.5 text-[11px] text-gray-500 data-[state=active]:text-white/80 tabular-nums">
                  {statusCounts.Enacted != null ? `(${statusCounts.Enacted})` : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                data-testid="filter-passed-house"
                value="passed-house"
                onClick={() => setFilterStatus("Passed House")}
                className="data-[state=active]:bg-[#2F6BFF] data-[state=active]:text-white text-white/70"
              >
                Passed House
                <span className="ml-1.5 text-[11px] text-gray-500 data-[state=active]:text-white/80 tabular-nums">
                  {statusCounts["Passed House"] != null ? `(${statusCounts["Passed House"]})` : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                data-testid="filter-passed-senate"
                value="passed-senate"
                onClick={() => setFilterStatus("Passed Senate")}
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-white/70"
              >
                Passed Senate
                <span className="ml-1.5 text-[11px] text-gray-500 data-[state=active]:text-white/80 tabular-nums">
                  {statusCounts["Passed Senate"] != null ? `(${statusCounts["Passed Senate"]})` : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                data-testid="filter-introduced"
                value="introduced"
                onClick={() => setFilterStatus("Introduced")}
                className="data-[state=active]:bg-white/15 data-[state=active]:text-white text-white/70"
              >
                Introduced
                <span className="ml-1.5 text-[11px] text-gray-500 data-[state=active]:text-white/80 tabular-nums">
                  {statusCounts.Introduced != null ? `(${statusCounts.Introduced})` : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                data-testid="filter-in-progress"
                value="in-progress"
                onClick={() => setFilterStatus("In Progress")}
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-white/70"
              >
                In Progress
                <span className="ml-1.5 text-[11px] text-gray-500 tabular-nums">
                  {statusCounts["In Progress"] != null ? `(${statusCounts["In Progress"]})` : ""}
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Bill-type sub-filter, centered to match status tabs */}
          <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mr-1">Bill type</span>
            {[
              { id: "all", label: "All types" },
              { id: "hr", label: "House Bill (HR)" },
              { id: "s", label: "Senate Bill (S)" },
              { id: "hjres", label: "House Joint Res." },
              { id: "sjres", label: "Senate Joint Res." },
              { id: "hres", label: "House Resolution" },
              { id: "sres", label: "Senate Resolution" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setBillType(t.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  billType === t.id
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white/5 text-white/70 border-white/10 hover:border-orange-500/40 hover:text-white"
                }`}
                data-testid={`bill-type-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Quick Views, one-click presets combining status + type filters.
              Active when the current (filterStatus, billType) pair matches. */}
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mr-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Quick views
            </span>
            {[
              { id: "enacted-all", label: "Enacted this Congress", status: "Enacted", type: "all", icon: Trophy },
              { id: "hr-waiting-senate", label: "Passed House → awaiting Senate", status: "Passed House", type: "hr", icon: ArrowRightLeft },
              { id: "s-waiting-house", label: "Passed Senate → awaiting House", status: "Passed Senate", type: "s", icon: ArrowLeftRight },
              { id: "hr-in-progress", label: "House bills moving", status: "In Progress", type: "hr", icon: Zap },
              { id: "s-in-progress", label: "Senate bills moving", status: "In Progress", type: "s", icon: Zap },
              { id: "jres-active", label: "Joint Resolutions in play", status: "In Progress", type: "hjres", icon: Scroll },
            ].map((qv) => {
              const active = !stalledMode && filterStatus === qv.status && billType === qv.type;
              const Icon = qv.icon;
              return (
                <button
                  key={qv.id}
                  type="button"
                  onClick={() => {
                    setStalledMode(false);
                    setFilterStatus(qv.status);
                    setBillType(qv.type);
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                    active
                      ? "whip-orange-bg text-white border-orange-500 shadow-[0_0_0_1px_rgba(255,138,0,0.4),0_0_24px_rgba(255,138,0,0.25)]"
                      : "bg-orange-500/10 text-orange-300 border-orange-500/30 hover:border-orange-400 hover:bg-orange-500/15"
                  }`}
                  data-testid={`quick-view-${qv.id}`}
                >
                  <Icon className="w-3 h-3" />
                  {qv.label}
                </button>
              );
            })}

            {/* Stalled & Forgotten, qualitatively different from the
                status/type Quick Views, so it gets its own purple chip.
                Only meaningful for the current Congress (forecast cache). */}
            <button
              type="button"
              disabled={congress !== 119 || stalledLocked}
              onClick={() => {
                setStalledMode((v) => !v);
                setFilterStatus("all");
                setBillType("all");
                setSearchQuery("");
              }}
              className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                congress !== 119 || stalledLocked
                  ? "bg-white/5 text-white/30 border-white/10 cursor-not-allowed"
                  : stalledMode
                  ? "bg-purple-600 text-white border-purple-500 shadow-[0_0_0_1px_rgba(168,85,247,0.4),0_0_24px_rgba(168,85,247,0.25)]"
                  : "bg-purple-500/10 text-purple-300 border-purple-500/30 hover:border-purple-400 hover:bg-purple-500/15"
              }`}
              data-testid="quick-view-stalled"
              title={
                stalledLocked
                  ? "Stalled & Forgotten is included in WHIP Pro, ask your workspace administrator about upgrading."
                  : congress !== 119
                  ? "Stalled & Forgotten uses live forecasts, only available for the 119th Congress."
                  : "Bills carrying the Stalled verdict, no recorded action in 6+ months, or pass probability down 20+ points since first snapshot."
              }
            >
              <Sparkles className="w-3 h-3" />
              Stalled{stalledLocked ? " · PRO" : ""}
            </button>
            {(stalledMode || filterStatus !== "all" || billType !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setFilterStatus("all");
                  setBillType("all");
                  setStalledMode(false);
                }}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-white/60 hover:border-white/30 hover:text-white flex items-center gap-1.5 transition-colors"
                data-testid="quick-view-reset"
              >
                <X className="w-3 h-3" /> Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Just Introduced drill-through banner */}
        {introducedWithin && !stalledMode && (
          <div
            className="mb-4 flex items-center justify-between rounded-lg border border-orange-500/40 bg-orange-500/10 px-4 py-3"
            data-testid="introduced-within-banner"
          >
            <p className="text-sm text-orange-200 font-semibold">
              <Sparkles className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Just Introduced, bills filed in the last {introducedWithin} hours, newest first
            </p>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("introducedWithin");
                setSearchParams(next, { replace: true });
              }}
              className="text-xs text-orange-300 hover:text-white border border-orange-500/40 rounded-full px-2.5 py-1 transition-colors"
              data-testid="introduced-within-clear"
            >
              Clear ✕
            </button>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-600" data-testid="results-count">
            {stalledMode ? (
              <>
                Showing <span className="font-semibold text-white">{stalledData.bills.length}</span> stalled bills.
                <span className="text-white/45 ml-1">
                  ({(stalledData.criteria?.minIdleDays ?? 90)}+ days idle OR ≥{(stalledData.criteria?.minDropPP ?? 20)}pp probability drop)
                </span>
              </>
            ) : (
              <>
                Showing{" "}
                <span className="font-semibold text-gray-900">
                  {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}
                  {"-"}
                  {Math.min(page * PAGE_SIZE, total)}
                </span>{" "}
                of <span className="font-semibold text-gray-900">{total.toLocaleString()}</span> bills
              </>
            )}
          </p>
          {loading && (bills.length > 0 || stalledData.bills.length > 0) && (
            <span className="text-xs text-gray-500 flex items-center gap-2">
              <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500" />
              Loading…
            </span>
          )}
        </div>

        {/* Stalled & Forgotten, purple-themed alternate grid */}
        {stalledMode && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 md:auto-rows-fr" data-testid="stalled-grid">
            {stalledData.bills.map((bill) => {
              const s = bill.staleness || {};
              return (
                <Link key={bill.billId} to={`/bill/${bill.billId}`} className="block h-full">
                <Card
                  data-testid={`stalled-card-${bill.billId}`}
                  className="p-6 h-full flex flex-col hover:shadow-xl transition-all duration-300 border border-purple-500/30 hover:border-purple-400 cursor-pointer rounded-xl"
                  style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.06), rgba(11,11,15,0.95))" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getTypeColor(bill.type)} border font-medium px-3 py-1`}>
                        {bill.billNumber}
                      </Badge>
                      <Badge className="bg-purple-500/15 text-purple-300 border-purple-500/30 border font-medium px-3 py-1">
                        Stalled
                      </Badge>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/35" />
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2">{bill.title}</h3>

                  <div className="flex items-center gap-3 text-sm text-white/70 mb-3 flex-wrap">
                    {s.daysSinceLastAction != null && (
                      <Badge variant="outline" className="bg-white/5 border-white/15 text-white/80 px-2 py-0.5">
                        Idle {s.daysSinceLastAction} days
                      </Badge>
                    )}
                    {s.dropPP > 0 && (
                      <Badge variant="outline" className="bg-rose-500/10 border-rose-500/30 text-rose-300 px-2 py-0.5">
                        Probability dropped {s.dropPP}pp
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10 text-sm text-white/55">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Last action: {s.lastActionDate || "n/a"}</span>
                    </div>
                    <div className="text-right">
                      {s.initialProbability != null && (
                        <span className="tabular-nums">
                          <span className="text-white/45">{Math.round(s.initialProbability * 100)}%</span>
                          <span className="text-white/35 mx-1">→</span>
                          <span className="text-rose-300 font-semibold">{Math.round((bill.currentProbability ?? 0) * 100)}%</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {bill.sponsor?.name && (
                    <div className="flex items-center gap-1 mt-3 text-xs text-white/45">
                      <User className="w-3.5 h-3.5" />
                      Sponsor: {bill.sponsor.name} ({bill.sponsor.party}-{bill.sponsor.state})
                    </div>
                  )}
                </Card>
                </Link>
              );
            })}
            {stalledData.bills.length === 0 && !loading && (
              <p className="col-span-full text-center text-white/55 py-12">
                No stalled bills detected in the cache yet. The forecast pre-warmer needs a few minutes to populate.
              </p>
            )}
          </div>
        )}

        {/* Bills Grid */}
        {!stalledMode && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 md:auto-rows-fr">
          {bills.map((bill) => (
            <Link key={bill.id} to={`/bill/${bill.id}`} className="block h-full">
            <Card
              data-testid={`bill-card-${bill.id}`}
              className="p-6 h-full flex flex-col hover:shadow-xl transition-all duration-300 border border-white/10 hover:border-orange-500/40 cursor-pointer rounded-xl"
              style={{ background: "var(--whip-surface)" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Badge className={`${getTypeColor(bill.type)} border font-medium px-3 py-1`}>
                    {bill.billNumber}
                  </Badge>
                  <Badge className={`${getStatusColor(bill.status)} border font-medium px-3 py-1`}>
                    {bill.status}
                  </Badge>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-1 line-clamp-2">{bill.title}</h3>
              {bill.popularTitle && bill.popularTitle !== bill.title && (
                <p className="text-sm text-orange-600 font-semibold mb-2" data-testid={`bill-card-popular-${bill.id}`}>
                  Also known as: {bill.popularTitle}
                </p>
              )}

              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{bill.summary}</p>

              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(bill.introducedDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>
                    {bill.sponsor.name} ({bill.sponsor.party}-{bill.sponsor.state})
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-auto" data-testid={`bill-card-chips-${bill.id}`}>
                {(() => {
                  // Primary policy area, filterable chip (backend attaches
                  // `policyArea` from the enrichment index; older payloads
                  // carried it as subjects[0]).
                  const area = bill.policyArea || bill.subjects?.[0] || null;
                  const isActive = policyArea !== "all" && area === policyArea;
                  if (!area) return null;
                  return (
                    <button
                      type="button"
                      onClick={(e) => {
                        // Don't open the bill detail when the chip is clicked.
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isActive) {
                          setPolicyArea(area);
                          // Bring the filter into view so users see what changed.
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                      title={
                        isActive
                          ? `Already filtering by ${area}`
                          : `Filter to "${area}" bills`
                      }
                      data-testid={`bill-card-policy-${bill.id}`}
                      className={
                        "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border font-semibold transition-colors " +
                        (isActive
                          ? "bg-orange-500/25 text-orange-200 border-orange-500/60 cursor-default"
                          : "bg-orange-500/10 text-orange-300 border-orange-500/30 hover:bg-orange-500/20 hover:border-orange-500/50 cursor-pointer")
                      }
                    >
                      <Tag className="w-3 h-3" />
                      {area}
                    </button>
                  );
                })()}
                {(bill.legislativeSubjects || []).slice(0, 4).map((subject, idx) => (
                  <span
                    key={idx}
                    title={subject}
                    data-testid={`bill-card-subject-${bill.id}-${idx}`}
                    className="inline-flex items-center text-xs px-2 py-1 rounded-md border bg-white/[0.03] text-white/60 border-white/12"
                  >
                    {subject}
                  </span>
                ))}
                {(bill.legislativeSubjects || []).length > 4 && (
                  <Badge variant="outline" className="text-xs px-2 py-1">
                    +{bill.legislativeSubjects.length - 4} more
                  </Badge>
                )}
              </div>
            </Card>
            </Link>
          ))}
        </div>
        )}

        {!stalledMode && !loading && bills.length === 0 && (
          <div className="text-center py-12" data-testid="bills-empty-state">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            {policyArea !== "all" ? (
              <>
                <p className="text-xl text-white/80">
                  No bills currently tagged{" "}
                  <span className="text-orange-300 font-semibold">"{policyArea}"</span>
                </p>
                <p className="text-white/45 mt-2">
                  The policy-area index syncs every hour. Try clearing the filter, or check back soon.
                </p>
                <button
                  type="button"
                  onClick={() => setPolicyArea("all")}
                  className="mt-4 px-4 py-2 rounded-lg bg-orange-500/20 text-orange-200 border border-orange-500/40 hover:bg-orange-500/30 transition-colors text-sm font-semibold"
                  data-testid="clear-policy-filter"
                >
                  Clear policy filter
                </button>
              </>
            ) : (
              <>
                <p className="text-xl text-white/80">No bills found matching your criteria</p>
                <p className="text-white/45 mt-2">Try adjusting your search or filters</p>
              </>
            )}
          </div>
        )}

        {/* Pagination */}
        {!stalledMode && total > PAGE_SIZE && (
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </main>
    </div>
  );
};

function Pagination({ page, totalPages, onChange }) {
  // Build a compact page list: 1 … (page-1) page (page+1) … totalPages
  const pages = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
  const visible = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

  return (
    <nav
      className="mt-10 flex items-center justify-center gap-1 flex-wrap"
      data-testid="bills-pagination"
      aria-label="Bills pagination"
    >
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        data-testid="bills-pagination-prev"
      >
        Previous
      </Button>
      {visible.map((p, i) => {
        const prev = visible[i - 1];
        const gap = prev !== undefined && p - prev > 1;
        return (
          <span key={p} className="flex items-center gap-1">
            {gap && <span className="px-1 text-gray-400">…</span>}
            <Button
              variant={p === page ? "default" : "outline"}
              size="sm"
              className={p === page ? "whip-orange-bg hover:opacity-90 border-orange-500" : ""}
              onClick={() => onChange(p)}
              data-testid={`bills-pagination-page-${p}`}
            >
              {p}
            </Button>
          </span>
        );
      })}
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        data-testid="bills-pagination-next"
      >
        Next
      </Button>
    </nav>
  );
}

export default Dashboard;
